/**
 * Notification Preferences Hook
 * Manages notification state and API interactions
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../services/api';
import { useAuthStore } from '../../../../stores/auth-store';
import { DEFAULT_PREFERENCES } from '../utils/constants';
import type {
  NotificationPreferences,
  NotificationEmail,
  UseNotificationPreferencesReturn,
} from '../types';

export function useNotificationPreferences(): UseNotificationPreferencesReturn {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  // Local state
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [emails, setEmails] = useState<NotificationEmail[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Fetch preferences
  const { data, isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => api.getNotificationPreferences(),
  });

  // Fetch notification emails
  const { data: emailsData } = useQuery({
    queryKey: ['notification-emails'],
    queryFn: async () => {
      // For now, use primary email from user
      // API endpoint for notification emails may need to be added
      return {
        emails: user?.email
          ? [
              {
                id: 'primary',
                email: user.email,
                isPrimary: true,
                verified: true,
                createdAt: new Date().toISOString(),
              },
            ]
          : [],
      };
    },
  });

  // Sync fetched preferences to state
  useEffect(() => {
    if (data?.preferences) {
      setPreferences({
        emailEnabled: data.preferences.emailPriceDrops || data.preferences.emailStockAlerts || false,
        emailDigestFrequency: data.preferences.emailDigestFrequency || DEFAULT_PREFERENCES.emailDigestFrequency,
        pushEnabled: data.preferences.pushEnabled || false,
        timezone: DEFAULT_PREFERENCES.timezone, // From mobile API
        notificationWindowStart: DEFAULT_PREFERENCES.notificationWindowStart,
        notificationWindowEnd: DEFAULT_PREFERENCES.notificationWindowEnd,
        promoEmailsEnabled: false, // New field
      });
    } else {
      setPreferences({ ...DEFAULT_PREFERENCES });
    }
  }, [data]);

  // Sync emails to state
  useEffect(() => {
    if (emailsData?.emails) {
      setEmails(emailsData.emails);
    }
  }, [emailsData]);

  // Update preference mutation
  const updateMutation = useMutation({
    mutationFn: async (prefs: Partial<NotificationPreferences>) => {
      // Map to existing API format
      const apiPrefs: Record<string, unknown> = {};

      if ('emailEnabled' in prefs) {
        apiPrefs.emailPriceDrops = prefs.emailEnabled;
        apiPrefs.emailStockAlerts = prefs.emailEnabled;
      }
      if ('emailDigestFrequency' in prefs) {
        apiPrefs.emailDigestFrequency = prefs.emailDigestFrequency;
      }
      if ('pushEnabled' in prefs) {
        apiPrefs.pushEnabled = prefs.pushEnabled;
      }
      // Quiet hours and promo emails may need new API endpoints
      if ('timezone' in prefs) {
        apiPrefs.timezone = prefs.timezone;
      }
      if ('notificationWindowStart' in prefs) {
        apiPrefs.notificationWindowStart = prefs.notificationWindowStart;
      }
      if ('notificationWindowEnd' in prefs) {
        apiPrefs.notificationWindowEnd = prefs.notificationWindowEnd;
      }
      if ('promoEmailsEnabled' in prefs) {
        apiPrefs.promoEmailsEnabled = prefs.promoEmailsEnabled;
      }

      return api.updateNotificationPreferences(apiPrefs);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to save preferences');
    },
  });

  // Update single preference
  const updatePreference = useCallback(
    async <K extends keyof NotificationPreferences>(
      key: K,
      value: NotificationPreferences[K]
    ) => {
      setError(null);
      setIsSaving(true);

      // Optimistic update
      setPreferences(prev => (prev ? { ...prev, [key]: value } : null));

      try {
        await updateMutation.mutateAsync({ [key]: value });
      } catch {
        // Revert on error - refetch will restore correct state
        queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      } finally {
        setIsSaving(false);
      }
    },
    [updateMutation, queryClient]
  );

  // Add notification email
  const addEmail = useCallback(async (email: string) => {
    setError(null);
    setIsSaving(true);

    try {
      // API call would go here
      // await api.addNotificationEmail(email);

      // Optimistic add
      const newEmail: NotificationEmail = {
        id: `temp-${Date.now()}`,
        email,
        isPrimary: false,
        verified: false,
        createdAt: new Date().toISOString(),
      };
      setEmails(prev => [...prev, newEmail]);

      queryClient.invalidateQueries({ queryKey: ['notification-emails'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add email');
    } finally {
      setIsSaving(false);
    }
  }, [queryClient]);

  // Remove notification email
  const removeEmail = useCallback(async (emailId: string) => {
    setError(null);
    setIsSaving(true);

    try {
      // API call would go here
      // await api.removeNotificationEmail(emailId);

      // Optimistic remove
      setEmails(prev => prev.filter(e => e.id !== emailId));

      queryClient.invalidateQueries({ queryKey: ['notification-emails'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove email');
    } finally {
      setIsSaving(false);
    }
  }, [queryClient]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    preferences,
    emails,
    isLoading,
    isSaving,
    error,
    saved,
    updatePreference,
    addEmail,
    removeEmail,
    clearError,
  };
}
